import { useCallback, useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { errorStatus, translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import {
  fetchContactByOwner,
  isMaskedPhone,
  revealContact,
  toContactValues,
  type ContactEntry,
  type ContactFieldValues,
  type ContactOwnerType,
} from './contactApi'

export interface OwnerContact {
  contact: ContactEntry | null
  loading: boolean
  /** So'rov yiqildi — «kontakt yo'q» EMAS (u 404 va u `contact === null`) */
  error: string | null
  /**
   * Server O'QISHNI rad etdi (403).
   *
   * ⚠️ {@link missing} bilan ARALASHTIRMANG. «Kontakt yo'q» — yozuv
   * yo'qligi, «ruxsat yo'q» — yozuv BOR bo'lishi mumkin, lekin
   * ko'rsatilmayapti. Ikkalasini bir xil ko'rsatish operatorni bo'sh
   * formani to'ldirishga yuborardi va u yerda yana 403 ga urilardi.
   */
  forbidden: boolean
  missing: boolean
  /** Niqoblangan raqam ochilmagan: blok faqat o'qish uchun va PUT yuborilmaydi */
  locked: boolean
  canRead: boolean
  canWrite: boolean
  canReveal: boolean
  reveal: (reason: string) => void
  revealing: boolean
  revealError: string | null
  /** Ochilgan to'liq raqamlar — formaga bir marta ko'chirish uchun */
  revealed: { phone: string | null; phoneAlt: string | null } | null
  /** Formaga to'ldiriladigan qiymatlar — ochilgan raqamlar bilan */
  values: ContactFieldValues
}

/**
 * Egasi bo'yicha kontakt + raqamni ochish holati.
 *
 * ⚠️ `locked` — bu yerdagi ASOSIY tushuncha. Server o'qish yo'lida
 * raqamni NIQOBLAB beradi (`+9989****567`), `PUT` esa to'liq
 * almashtirish. Ya'ni niqoblangan qiymat bilan saqlash haqiqiy raqamni
 * niqob bilan almashtirib qo'yardi. Shuning uchun raqam ochilmaguncha
 * kontakt bloki faqat o'qish uchun bo'ladi va so'rov umuman yuborilmaydi.
 */
export function useOwnerContact(
  ownerType: ContactOwnerType,
  ownerId: string,
  enabled: boolean,
): OwnerContact {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canRead = can('contact.read')

  const query = useQuery({
    queryKey: queryKeys.contacts.byOwner(ownerType, ownerId),
    queryFn: () => fetchContactByOwner(ownerType, ownerId),
    enabled: enabled && canRead,
  })

  const contact = query.data ?? null
  const [revealed, setRevealed] = useState<{ phone: string | null; phoneAlt: string | null } | null>(
    null,
  )
  const [revealing, setRevealing] = useState(false)
  const [revealError, setRevealError] = useState<string | null>(null)

  // Oyna yopilib qayta ochilganda ochilgan raqam ekranda QOLMAYDI: u
  // alohida huquq bilan, sabab yozib olingan va shuning uchun ham
  // "bir marta" ko'rsatiladi.
  useEffect(() => {
    if (enabled) return
    setRevealed(null)
    setRevealError(null)
  }, [enabled])

  const reveal = useCallback(
    (reason: string) => {
      if (contact === null) return
      setRevealing(true)
      setRevealError(null)
      revealContact(contact.id, reason)
        .then((result) => setRevealed(result))
        .catch((error: unknown) => setRevealError(translateError(error, t)))
        .finally(() => setRevealing(false))
    },
    [contact, t],
  )

  const maskedPhone = isMaskedPhone(contact?.phone) || isMaskedPhone(contact?.phoneAlt)
  const locked = maskedPhone && revealed === null

  const base = toContactValues(contact)
  const values =
    revealed === null
      ? base
      : { ...base, phone: revealed.phone ?? '', phoneAlt: revealed.phoneAlt ?? '' }

  return {
    contact,
    loading: query.isPending && enabled && canRead,
    error: query.isError ? translateError(query.error, t) : null,
    forbidden: query.isError && errorStatus(query.error) === 403,
    missing: canRead && !query.isPending && !query.isError && contact === null,
    locked,
    canRead,
    canWrite: can('contact.write'),
    canReveal: can('contact.reveal'),
    reveal,
    revealing,
    revealError,
    revealed,
    values,
  }
}
